/**
 * Sentry Configuration
 * Initializes error tracking, performance monitoring, session replay, and structured logging.
 * Must be imported before any other app code in index.tsx.
 */
import * as Sentry from "@sentry/react";
import { isDemoHost } from "./demo";
import { normalizeStartSpan, scrubBreadcrumb, scrubEvent, scrubSpan, tracesSampleRateFor } from "./sentryScrubbing";

const dsn = process.env.REACT_APP_SENTRY_DSN;
// The Live sample (PRD 117) is the production build on its own host; tagging
// it `demo` keeps sample-visitor noise out of production's issue stream.
const environment = isDemoHost() ? "demo" : (process.env.NODE_ENV || "development");
const isProduction = environment === "production" || environment === "demo";

Sentry.init({
    dsn,

    // Enable in any environment as long as a DSN is provided
    enabled: !!dsn,

    // Tag events with the current environment (development / production)
    environment,

    // Send PII for richer error context (user email, IP, etc.)
    sendDefaultPii: true,

    // Enable structured logs
    enableLogs: true,

    // Performance — 100% in dev for visibility, 10% in production unless
    // REACT_APP_SENTRY_TRACES_SAMPLE_RATE says otherwise. Errors are not
    // sampled by this.
    tracesSampleRate: tracesSampleRateFor(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, isProduction),

    // URLs carry record ids and the public pages' capability tokens
    // (/pay/:token, /w9/:token…); every URL Sentry records on its own is
    // normalised on the way out (sentryScrubbing).
    beforeSend: scrubEvent,
    beforeSendSpan: scrubSpan,
    beforeBreadcrumb: scrubBreadcrumb,

    // Session Replay — higher sampling in dev for debugging
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
        Sentry.browserTracingIntegration({ beforeStartSpan: normalizeStartSpan }),
        Sentry.replayIntegration(),
        Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
});

export default Sentry;
